import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '@mdi/react';
import { mdiTicketOutline, mdiTicketAccount, mdiChartBar, mdiCog } from '@mdi/js';
import AppHeader from '../../components/AppHeader';
import { ticketApi } from '../../api/ticket';
import { formatCurrencyFromMinor } from '../../utils/currency';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';

const FILTERS = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'month',    label: 'This Month' },
    { key: 'year',     label: 'This Year' },
    { key: 'all',      label: 'All Time' },
];

const applyFilter = (stats, filter) => {
    const now = new Date();
    if (filter === 'upcoming') {
        return stats.filter(e => e.eventStartDateTime && new Date(e.eventStartDateTime) >= now);
    }
    if (filter === 'month') {
        return stats.filter(e => {
            if (!e.eventStartDateTime) return false;
            const d = new Date(e.eventStartDateTime);
            return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        });
    }
    if (filter === 'year') {
        return stats.filter(e => {
            if (!e.eventStartDateTime) return false;
            return new Date(e.eventStartDateTime).getFullYear() === now.getFullYear();
        });
    }
    return stats; // 'all'
};

/**
 * TicketingPortal Page - Ticketing Management Landing Page
 *
 * Central hub for event ticketing functions
 */
const TicketingPortal = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [allStats, setAllStats] = useState(null); // null = loading
    const [filter, setFilter] = useState('upcoming');

    useEffect(() => {
        if (!user?.organisationId) return;
        ticketApi.getStats().then(setAllStats).catch(() => setAllStats([]));
    }, [user?.organisationId]);

    const filtered = useMemo(() => applyFilter(allStats || [], filter), [allStats, filter]);

    const totalSold    = filtered.reduce((sum, e) => sum + e.totalSold, 0);
    const totalRevenue = filtered.reduce((sum, e) => sum + e.totalRevenue, 0);
    const eventCount   = filtered.length;
    const currency     = (allStats || [])[0]?.currency || 'AUD';

    const formatRevenue = (amount) => formatCurrencyFromMinor(amount, currency);

    const loading = allStats === null;

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: '800px' }}>
                    {/* Portal Header Card */}
                    <div className="sub-portal-header-card" style={{ padding: 24 }}>
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="back-nav-btn"
                            >
                                ← Dashboard
                            </button>
                            <h1 className="sub-portal-header-title">
                                Ticketing
                            </h1>
                            <button
                                className="dashboard-header-settings-btn"
                                onClick={() => navigate('/ticketing/settings')}
                                aria-label="Ticketing settings"
                                title="Ticketing settings"
                                style={{ position: 'absolute', right: 0, left: 'auto' }}
                            >
                                <Icon path={mdiCog} size={1.1} color="var(--dl-color-purple-default)" />
                            </button>
                        </div>

                        <p className="sub-portal-header-subtitle">
                            Create and manage event tickets
                        </p>

                        <div className="portal-metrics" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            {/* Filter pills — left */}
                            <div className="dashboard-tabs" style={{ marginTop: 0 }}>
                                {FILTERS.map(f => (
                                    <button
                                        key={f.key}
                                        className={`dashboard-tab${filter === f.key ? ' active' : ''}`}
                                        onClick={() => setFilter(f.key)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>

                            {/* Metrics — right */}
                            <div style={{ display: 'flex', gap: 30, marginRight: 12 }}>
                                <div className="portal-metric-item">
                                    <span className="portal-metric-value">
                                        {loading ? '—' : totalSold}
                                    </span>
                                    <span className="portal-metric-label">Tickets Sold</span>
                                </div>
                                <div className="portal-metric-item">
                                    <span className="portal-metric-value">
                                        {loading ? '—' : formatRevenue(totalRevenue)}
                                    </span>
                                    <span className="portal-metric-label">Total Revenue</span>
                                </div>
                                <div className="portal-metric-item">
                                    <span className="portal-metric-value">
                                        {loading ? '—' : eventCount}
                                    </span>
                                    <span className="portal-metric-label">Events</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Function Cards Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '20px'
                    }}>
                        <Link
                            to="/create-tickets"
                            className="content-card nav-card"
                            style={{ padding: '30px 24px' }}
                        >
                            <div className="module-card-header">
                                <h3>Create Tickets</h3>
                                <Icon path={mdiTicketOutline} size={1.1} color="#451f78" />
                            </div>
                            <p className="module-card-subtitle">
                                Set up ticketing for your dance events and workshops
                            </p>
                        </Link>

                        <Link
                            to="/ticketing/manage"
                            className="content-card nav-card"
                            style={{ padding: '30px 24px' }}
                        >
                            <div className="module-card-header">
                                <h3>Manage Tickets</h3>
                                <Icon path={mdiTicketAccount} size={1.1} color="#451f78" />
                            </div>
                            <p className="module-card-subtitle">
                                View ticket sales, configure settings, and track revenue
                            </p>
                        </Link>

                        <Link
                            to="/ticketing/stats"
                            className="content-card nav-card"
                            style={{ padding: '30px 24px' }}
                        >
                            <div className="module-card-header">
                                <h3>Ticket Stats</h3>
                                <Icon path={mdiChartBar} size={1.1} color="#451f78" />
                            </div>
                            <p className="module-card-subtitle">
                                View sales metrics, capacity, and revenue for published events
                            </p>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketingPortal;
